<script setup lang="ts">
import device from '@@/bits/device'
import { useSurfaceStore } from '@@/pinia/surface'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsTable from '@@/vuecomponents/SurfacePostsTable.vue'
import { POST_ROW_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostsTableRow.vue'
import { SECTION_HEADER_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostsTableSectionHeader.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { storeToRefs } from 'pinia'
import type { CSSProperties } from 'vue'
import { computed, ref } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

if (!device.mobile) {
  useDoubleClickToPost()
}

const surfaceStore = useSurfaceStore()
const { xHeader, backgroundLuminance, isScreenshotMode, skinPrimaryVariantColorAsRGB, isSkinColorAvailable } =
  storeToRefs(surfaceStore)

const containerStyle = computed<CSSProperties>(() => {
  if (isSkinColorAvailable.value && skinPrimaryVariantColorAsRGB.value) {
    return { '--scrollbar-skin-color': skinPrimaryVariantColorAsRGB.value }
  }
  return {}
})

const containerRef = ref<HTMLDivElement | null>(null)
useAutoScrollElement({
  scrollContainerElementRef: containerRef,
  type: [POST_ROW_DRAGGABLE_TYPE, SECTION_HEADER_DRAGGABLE_TYPE],
})
</script>

<template>
  <div
    id="wall-container"
    ref="containerRef"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'scrollbar-skin': isSkinColorAvailable,
      'overflow-auto': !isScreenshotMode,
    }"
    :style="containerStyle"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" class="sticky inset-x-0 z-10" />

    <SurfacePostsTable
      :class="['mt-3 tablet-portrait:mt-4 pb-8', 'grow']"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
  </div>
</template>

<style scoped>
/* Overrides the colors in scrollbar-photo classes */
.scrollbar-skin::-webkit-scrollbar-track {
  background-color: var(--scrollbar-skin-color);
}

.scrollbar-skin::-webkit-scrollbar-corner {
  background: var(--scrollbar-skin-color);
}

.scrollbar-photo::-webkit-scrollbar,
.scrollbar-photo-dark::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
</style>
